import React, { useState, useCallback, useEffect, useRef } from 'react';
import { MetadataStepData, LocationData, SubmissionWorkflow } from '../../types/submission-workflow';
import AddressAutocomplete from '../AddressAutocomplete';
import { fetchAddressDetailsFromCoordinates } from '../../utils/reverseGeocode';

interface MetadataEntryStepProps {
  workflow: SubmissionWorkflow;
  onUpdateMetadata: (metadata: Partial<MetadataStepData>) => void;
  onNext: () => void;
  onPrevious: () => void;
}

export const MetadataEntryStep: React.FC<MetadataEntryStepProps> = ({
  workflow,
  onUpdateMetadata,
  onNext,
  onPrevious
}) => {
  const metadata = workflow.data.metadata;

  // Early return if metadata is not available
  if (!metadata) {
    return (
      <div className="text-center py-8">
        <p className="text-urban-300">Loading metadata...</p>
      </div>
    );
  }

  // Check if we have technical errors to show friendly message
  const technicalErrors = workflow.data.aiAnalysis?.rejectedImages?.filter(
    img => img.errorType === 'technical_error'
  ) || [];
  const contentViolations = workflow.data.aiAnalysis?.rejectedImages?.filter(
    img => img.errorType === 'content_violation'
  ) || [];
  const hasAISuggestions = workflow.data.aiAnalysis?.combinedSuggestions &&
    (workflow.data.aiAnalysis.combinedSuggestions.tags.length > 0 ||
      workflow.data.aiAnalysis.combinedSuggestions.categories.length > 0);
  const [currentTag, setCurrentTag] = useState('');
  const [currentCategory, setCurrentCategory] = useState('');
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const validationInProgress = useRef(false);
  const lastValidationResult = useRef<string>('');

  // Validate metadata with debouncing and loop prevention
  const validateMetadata = useCallback(() => {
    if (validationInProgress.current) return;

    console.log('🔍 Validating metadata:', {
      tags: metadata.tags,
      categories: metadata.categories,
      location: metadata.location,
      pickupDate: metadata.pickupDate,
      pickupDateType: metadata.pickupDate ? typeof metadata.pickupDate : 'undefined',
      pickupDateValid: metadata.pickupDate instanceof Date ? !isNaN(metadata.pickupDate.getTime()) : 'not a date'
    });

    validationInProgress.current = true;

    // Basic validation logic
    const errors: string[] = [];

    // Location validation
    if (!metadata.location.address?.trim()) {
      errors.push('Street address is required');
    }
    if (!metadata.location.suburb?.trim()) {
      errors.push('Suburb is required');
    }
    if (!metadata.location.council?.trim()) {
      errors.push('Council is required');
    }
    if (!metadata.location.postcode?.trim()) {
      errors.push('Postcode is required');
    } else if (!/^\d{4}$/.test(metadata.location.postcode.trim())) {
      errors.push('Postcode must be 4 digits');
    }

    // Tags validation (at least one tag required)
    if (metadata.tags.length === 0) {
      errors.push('At least one tag is required');
    }

    console.log('📋 Validation results:', {
      errors: errors || [],
      isValid: errors?.length === 0
    });

    const validationResultKey = JSON.stringify({
      errors: errors || [],
      isValid: errors?.length === 0
    });

    // Only update if validation results actually changed
    if (validationResultKey !== lastValidationResult.current) {
      console.log('✅ Validation results changed, updating metadata');
      lastValidationResult.current = validationResultKey;
      onUpdateMetadata({
        isValid: errors?.length === 0,
        validationErrors: errors || []
      });
    } else {
      console.log('⏭️ Validation results unchanged, skipping update');
    }

    validationInProgress.current = false;
  }, [metadata.tags, metadata.categories, metadata.location, metadata.pickupDate]);

  // Initialize metadata with AI suggestions on first load (only once)
  const [hasInitializedAI, setHasInitializedAI] = useState(false);

  useEffect(() => {
    if (!hasInitializedAI &&
      workflow.data.aiAnalysis?.combinedSuggestions &&
      workflow.data.aiAnalysis.combinedSuggestions.tags.length > 0 &&
      metadata.tags.length === 0 &&
      metadata.categories.length === 0) {
      console.log('🤖 Initializing metadata with AI suggestions:', workflow.data.aiAnalysis.combinedSuggestions);

      onUpdateMetadata({
        tags: [...workflow.data.aiAnalysis.combinedSuggestions.tags],
        categories: [...workflow.data.aiAnalysis.combinedSuggestions.categories]
      });

      setHasInitializedAI(true);
    }
  }, [workflow.data.aiAnalysis?.combinedSuggestions, metadata.tags.length, metadata.categories.length, hasInitializedAI]);

  // Debounced validation to prevent excessive calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      validateMetadata();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [metadata.tags, metadata.categories, metadata.location, metadata.pickupDate]);

  // Tag management
  const addTag = useCallback(() => {
    if (currentTag.trim() && !metadata.tags.includes(currentTag.trim().toLowerCase())) {
      onUpdateMetadata({
        tags: [...metadata.tags, currentTag.trim().toLowerCase()]
      });
      setCurrentTag('');
    }
  }, [currentTag, metadata.tags, onUpdateMetadata]);

  const removeTag = useCallback((tagToRemove: string) => {
    onUpdateMetadata({
      tags: metadata.tags.filter(tag => tag !== tagToRemove)
    });
  }, [metadata.tags, onUpdateMetadata]);

  // Category management
  const addCategory = useCallback(() => {
    if (currentCategory.trim() && !metadata.categories.includes(currentCategory.trim().toLowerCase())) {
      onUpdateMetadata({
        categories: [...metadata.categories, currentCategory.trim().toLowerCase()]
      });
      setCurrentCategory('');
    }
  }, [currentCategory, metadata.categories, onUpdateMetadata]);

  const removeCategory = useCallback((categoryToRemove: string) => {
    onUpdateMetadata({
      categories: metadata.categories.filter(category => category !== categoryToRemove)
    });
  }, [metadata.categories, onUpdateMetadata]);

  // Location management
  const handleLocationChange = useCallback((field: keyof LocationData, value: any) => {
    onUpdateMetadata({
      location: {
        ...metadata.location,
        [field]: value
      }
    });
  }, [metadata.location, onUpdateMetadata]);

  const handleAddressSelect = useCallback((addressDetails: {
    address: string;
    suburb: string;
    council: string;
    postcode: string;
    latitude: number;
    longitude: number;
  }) => {
    console.log('📍 Address selected:', addressDetails);

    const locationUpdate = {
      location: {
        ...metadata.location,
        address: addressDetails.address,
        suburb: addressDetails.suburb,
        council: addressDetails.council,
        postcode: addressDetails.postcode,
        coordinates: {
          lat: addressDetails.latitude,
          lng: addressDetails.longitude
        }
      }
    };

    console.log('📍 Updating location metadata:', locationUpdate);
    onUpdateMetadata(locationUpdate);
  }, [metadata.location, onUpdateMetadata]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    setIsLocationLoading(true);

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by this browser.');
      setIsLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        onUpdateMetadata({
          location: {
            ...metadata.location,
            coordinates: { lat: latitude, lng: longitude }
          }
        });

        try {
          const {
            address,
            suburb,
            council,
            postcode
          } = await fetchAddressDetailsFromCoordinates(latitude, longitude);

          onUpdateMetadata({
            location: {
              ...metadata.location,
              coordinates: { lat: latitude, lng: longitude },
              address,
              suburb,
              council,
              postcode
            }
          });
        } catch (error) {
          console.error('Error getting address details:', error);
        }

        setIsLocationLoading(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        alert('Unable to get your location. Please enter the address manually.');
        setIsLocationLoading(false);
      }
    );
  }, [metadata.location, onUpdateMetadata]);

  // Pickup date management
  const handlePickupDateChange = useCallback((dateString: string) => {
    console.log('📅 Pickup date change:', { dateString, type: typeof dateString });

    if (!dateString || dateString.trim() === '') {
      console.log('📅 Clearing pickup date');
      onUpdateMetadata({ pickupDate: undefined });
      return;
    }

    try {
      const date = new Date(dateString);
      console.log('📅 Parsed date:', { date, isValid: !isNaN(date.getTime()) });

      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('❌ Invalid date string provided:', dateString);
        return;
      }

      console.log('📅 Setting valid pickup date:', date.toISOString());
      onUpdateMetadata({ pickupDate: date });
    } catch (error) {
      console.error('❌ Error parsing date:', error);
    }
  }, [onUpdateMetadata]);

  // Handle submit button click
  const handleSubmit = useCallback(() => {
    if (metadata.isValid) {
      onNext(); // This will trigger the submission in the parent component
    }
  }, [metadata.isValid, onNext]);

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center">
        <h2 className="text-2xl font-bold text-urban mb-2">📝 Add Details</h2>
        <p className="text-urban-300">
          Review and edit the details for your curbside treasures
        </p>

        {/* Show technical error message if AI couldn't process images */}
        {technicalErrors.length > 0 && (
          <div className="ai-message technical-error mt-4">
            <div className="message-icon">🦜</div>
            <div className="message-content">
              <p><strong>Our parrot spotter flew off for a snack!</strong></p>
              <p>Please tag your treasures manually. Don't worry - our moderators will still review your post.</p>
            </div>
          </div>
        )}

        {/* Show content violation warnings */}
        {contentViolations.length > 0 && (
          <div className="ai-message content-violation mt-4">
            <div className="message-icon">⚠️</div>
            <div className="message-content">
              <p><strong>Some images need attention:</strong></p>
              {contentViolations.map((violation, index) => (
                <p key={index} className="violation-message">
                  • {violation.userMessage || violation.reason}
                </p>
              ))}
            </div>
          </div>
        )}

        {/* Show success message when AI worked */}
        {hasAISuggestions && technicalErrors.length === 0 && (
          <div className="ai-message success mt-4">
            <div className="message-icon">✨</div>
            <div className="message-content">
              <p>Our AI spotted some details about your treasures! Feel free to edit or add more.</p>
            </div>
          </div>
        )}
      </div>

      {/* Photos Section - Always show uploaded/taken photos */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <h3 className="font-medium text-urban mb-4">📸 Your Photos</h3>

        {workflow.data.photos && workflow.data.photos.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
            {workflow.data.photos.map((photo, index) => (
              <div key={photo.id} className="relative group">
                <img
                  src={photo.url}
                  alt={`Treasure photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-urban-200 hover:border-cardboard transition-colors"
                />

                {/* Photo source badge */}
                <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                  {photo.source.type === 'camera' ? '📷' : '📁'}
                </div>

                {/* Upload status badge */}
                {photo.isUploaded && (
                  <div className="absolute top-2 right-2 bg-verge text-white text-xs px-2 py-1 rounded">
                    ✓
                  </div>
                )}

                {/* Photo info on hover */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 rounded-lg flex items-center justify-center">
                  <span className="text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity">
                    Photo {index + 1}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-urban-300">
            <div className="text-4xl mb-2">📸</div>
            <p className="text-sm">No photos available</p>
            <p className="text-xs mt-1">Go back to add photos</p>
          </div>
        )}

        {/* Rejected Images Section */}
        {(contentViolations.length > 0 || technicalErrors.length > 0) && (
          <div className="mt-6 pt-6 border-t border-urban-200">
            <h4 className="font-medium text-urban mb-4">⚠️ Image Issues</h4>

            {contentViolations.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-rust-800 mb-2">⚠️ Images with Issues</h4>
                <div className="space-y-2">
                  {contentViolations.map((violation, index) => (
                    <div key={index} className="bg-rust-50 border border-rust-200 rounded-lg p-3">
                      <p className="text-sm text-rust-700">
                        <strong>Image {index + 1}:</strong> {violation.userMessage || violation.reason}
                      </p>
                      <p className="text-xs text-rust-600 mt-1">
                        Please go back and upload a different image to replace this one.
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {technicalErrors.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium text-treasure-800 mb-2">🦜 AI Processing Issues</h4>
                <div className="space-y-2">
                  {technicalErrors.map((error, index) => (
                    <div key={index} className="bg-treasure-50 border border-treasure-200 rounded-lg p-3">
                      <p className="text-sm text-treasure-700">
                        <strong>Image {index + 1}:</strong> {error.userMessage || error.reason}
                      </p>
                      <p className="text-xs text-treasure-600 mt-1">
                        This image will still be included in your report.
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Validation Errors */}
      {metadata.validationErrors.length > 0 && (
        <div className="bg-rust-50 border border-rust-200 rounded-lg p-4">
          <h4 className="font-medium text-rust-800 mb-2">⚠️ Please fix the following issues:</h4>
          <ul className="text-rust-700 list-disc list-inside space-y-1">
            {metadata.validationErrors.map((error, index) => (
              <li key={index} className="text-sm">{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tags Section */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-urban">🏷️ Tags</h3>
          {hasAISuggestions && (
            <div className="flex items-center text-xs text-treasure-600">
              <span className="mr-1">✨</span>
              AI suggested
            </div>
          )}
        </div>

        {/* Add Tag Input */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={currentTag}
            onChange={(e) => setCurrentTag(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
            className="flex-1 px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
            placeholder="Add a tag (e.g., furniture, chair, wooden)"
            maxLength={30}
          />
          <button
            type="button"
            onClick={addTag}
            className="px-4 py-2 bg-cardboard text-white rounded-md hover:bg-cardboard-600 transition-colors"
          >
            Add
          </button>
        </div>

        {/* Current Tags */}
        {metadata.tags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {metadata.tags.map((tag, index) => {
              const isAIGenerated = workflow.data.aiAnalysis?.combinedSuggestions?.tags.includes(tag);
              return (
                <span
                  key={index}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${isAIGenerated
                      ? 'bg-treasure-100 text-treasure-800 border border-treasure-200'
                      : 'bg-verge text-white'
                    }`}
                >
                  {isAIGenerated && <span className="mr-1 text-xs">✨</span>}
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className={`ml-2 transition-colors ${isAIGenerated
                        ? 'text-treasure-600 hover:text-treasure-800'
                        : 'text-white hover:text-verge-200'
                      }`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-urban-300 text-sm">No tags added yet</p>
        )}
      </div>

      {/* Categories Section */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-urban">📂 Categories</h3>
          {hasAISuggestions && (
            <div className="flex items-center text-xs text-treasure-600">
              <span className="mr-1">✨</span>
              AI suggested
            </div>
          )}
        </div>

        {/* Add Category Input */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={currentCategory}
            onChange={(e) => setCurrentCategory(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
            className="flex-1 px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
            placeholder="Add a category (e.g., furniture, electronics, books)"
            maxLength={30}
          />
          <button
            type="button"
            onClick={addCategory}
            className="px-4 py-2 bg-cardboard text-white rounded-md hover:bg-cardboard-600 transition-colors"
          >
            Add
          </button>
        </div>

        {/* Current Categories */}
        {metadata.categories.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {metadata.categories.map((category, index) => {
              const isAIGenerated = workflow.data.aiAnalysis?.combinedSuggestions?.categories.includes(category);
              return (
                <span
                  key={index}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${isAIGenerated
                      ? 'bg-treasure-100 text-treasure-800 border border-treasure-200'
                      : 'bg-cardboard text-white'
                    }`}
                >
                  {isAIGenerated && <span className="mr-1 text-xs">✨</span>}
                  {category}
                  <button
                    type="button"
                    onClick={() => removeCategory(category)}
                    className={`ml-2 transition-colors ${isAIGenerated
                        ? 'text-treasure-600 hover:text-treasure-800'
                        : 'text-white hover:text-cardboard-200'
                      }`}
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        ) : (
          <p className="text-urban-300 text-sm">No categories added yet</p>
        )}
      </div>

      {/* Location Section */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-urban">📍 Location</h3>
          <button
            type="button"
            onClick={getCurrentLocation}
            disabled={isLocationLoading}
            className="px-3 py-1 bg-verge text-white rounded-md hover:bg-verge-600 disabled:opacity-50 text-sm transition-colors"
          >
            {isLocationLoading ? 'Getting location...' : 'Use current location'}
          </button>
        </div>

        {/* Address Autocomplete */}
        <div className="mb-4">
          <AddressAutocomplete
            onAddressSelect={handleAddressSelect}
            placeholder="Start typing an address..."
            initialValue={metadata.location.address || ''}
          />
        </div>

        {/* Location Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-urban-300 mb-1">Street Address</label>
            <input
              type="text"
              value={metadata.location.address || ''}
              onChange={(e) => handleLocationChange('address', e.target.value)}
              placeholder="Street address"
              className="w-full px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-urban-300 mb-1">Suburb</label>
            <input
              type="text"
              value={metadata.location.suburb || ''}
              onChange={(e) => handleLocationChange('suburb', e.target.value)}
              placeholder="Suburb"
              className="w-full px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-urban-300 mb-1">Council</label>
            <input
              type="text"
              value={metadata.location.council || ''}
              onChange={(e) => handleLocationChange('council', e.target.value)}
              placeholder="Council"
              className="w-full px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-urban-300 mb-1">Postcode</label>
            <input
              type="text"
              value={metadata.location.postcode || ''}
              onChange={(e) => handleLocationChange('postcode', e.target.value)}
              placeholder="Postcode"
              pattern="[0-9]{4}"
              className="w-full px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
              required
            />
          </div>
        </div>

        {/* Coordinates Display */}
        {metadata.location.coordinates && (
          <div className="mt-4 text-xs text-urban-300">
            Coordinates: {metadata.location.coordinates.lat.toFixed(6)}, {metadata.location.coordinates.lng.toFixed(6)}
          </div>
        )}
      </div>

      {/* Pickup Date Section */}
      <div className="bg-white rounded-lg border border-urban-200 p-6">
        <h3 className="font-medium text-urban mb-4">📅 Pickup Date (Optional)</h3>

        <div className="space-y-4">
          <input
            type="date"
            value={metadata.pickupDate ? metadata.pickupDate.toISOString().split('T')[0] : ''}
            onChange={(e) => handlePickupDateChange(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="px-3 py-2 bg-sky-neutral border border-urban-300 rounded-md focus:outline-none focus:border-cardboard focus:ring-2 focus:ring-treasure-200"
          />
          <p className="text-sm text-urban-300">
            If not specified, we'll assume pickup in 7 days
          </p>
        </div>
      </div>

      {/* Form Status */}
      <div className={`rounded-lg p-4 ${metadata.isValid
          ? 'bg-verge-50 border border-verge-200'
          : 'bg-treasure-50 border border-treasure-200'
        }`}>
        <div className="flex items-center space-x-2">
          <span className="text-lg">
            {metadata.isValid ? '✅' : '⏳'}
          </span>
          <div>
            <h4 className={`font-medium ${metadata.isValid ? 'text-verge-800' : 'text-treasure-800'
              }`}>
              {metadata.isValid ? 'Ready to Submit' : 'Complete Required Fields'}
            </h4>
            <p className={`text-sm ${metadata.isValid ? 'text-verge-700' : 'text-treasure-700'
              }`}>
              {metadata.isValid
                ? 'All required information has been provided'
                : 'Please fill in all required fields to continue'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="bg-cardboard-50 border border-cardboard-200 rounded-lg p-4">
        <h4 className="font-medium text-cardboard-800 mb-2">💡 Tips for Better Listings</h4>
        <ul className="text-sm text-cardboard-700 space-y-1">
          <li>• Use specific tags that describe the items (e.g., "wooden chair" vs just "chair")</li>
          <li>• Categories help people find items faster (furniture, electronics, books, etc.)</li>
          <li>• Accurate location helps treasure hunters find your items</li>
          <li>• Adding a pickup date helps people know when items will be available</li>
        </ul>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <button
          type="button"
          onClick={onPrevious}
          className="px-6 py-3 bg-urban-200 text-urban-700 rounded-lg hover:bg-urban-300 transition-colors font-medium"
        >
          ← Back to Photos
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!metadata.isValid}
          className="px-6 py-3 bg-verge text-white rounded-lg hover:bg-verge-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          ✓ Submit Report
        </button>
      </div>

      {/* AI Message Styles */}
      <style>{`
        .ai-message {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 16px;
          border-radius: 8px;
          font-size: 14px;
          line-height: 1.4;
          text-align: left;
        }
        .ai-message.technical-error {
          background-color: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
        }
        .ai-message.content-violation {
          background-color: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }
        .ai-message.success {
          background-color: #d1edff;
          border: 1px solid #bee5eb;
          color: #0c5460;
        }
        .message-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .message-content {
          flex: 1;
        }
        .message-content p {
          margin: 0 0 8px 0;
        }
        .message-content p:last-child {
          margin-bottom: 0;
        }
        .violation-message {
          font-size: 13px;
          margin-left: 8px;
        }
      `}</style>
    </div>
  );
};
